import React, { useEffect } from "react";
import { CheckCircle, Users, FileText } from "lucide-react";

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("sr-visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".sr").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const Requirements = () => {
  useScrollReveal();

  const eligibility = [
    "Must be a resident of Igembe South Constituency",
    "Currently enrolled in a recognized secondary school, college, or university",
    "Must demonstrate financial need",
    "Provide proof of admission or current school identification",
    "Must not be benefiting from another full scholarship (if applicable)",
  ];

  const documents = [
    "Copy of Student Birth Certificate or National ID",
    "Copy of Parent/Guardian National ID",
    "Official School Admission Letter",
    "Recent Fee Structure or School Invoice",
    "Academic Performance Report (latest term/semester)",
  ];

  const notes = [
    "All documents must be clear and legible.",
    "Incomplete applications may not be considered.",
    "Submission does not guarantee approval.",
    "Deadline for application must be strictly observed.",
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-6xl mx-auto px-6">

        {/* Title */}
        <div className="sr text-center mb-12" style={{ transitionDelay: "0s" }}>
          <h1 className="text-4xl font-bold mb-4">Application Requirements</h1>
          <p className="text-center text-gray-600 max-w-2xl mx-auto">
            Please review the eligibility criteria and required documents carefully
            before submitting your application. Ensure all documents are valid and up to date.
          </p>
          <div className="w-24 h-1 bg-blue-700 mx-auto rounded-full mt-6" />
        </div>

        <div className="grid md:grid-cols-2 gap-8">

          {/* Eligibility Criteria */}
          <div
            className="sr group bg-white rounded-2xl shadow-md p-8 flex flex-col items-start gap-4 border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300"
            style={{ transitionDelay: "0.1s" }}
          >
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <CheckCircle size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Eligibility Criteria</h2>
            <ul className="text-gray-700 space-y-3 w-full">
              {eligibility.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Required Documents */}
          <div
            className="sr group bg-white rounded-2xl shadow-md p-8 flex flex-col items-start gap-4 border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300"
            style={{ transitionDelay: "0.18s" }}
          >
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <Users size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Required Documents</h2>
            <ul className="text-gray-700 space-y-3 w-full">
              {documents.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Important Notes */}
        <div
          className="sr mt-10 bg-blue-50 border border-blue-200 rounded-2xl p-8 flex items-start gap-5 hover:shadow-md transition-shadow duration-300"
          style={{ transitionDelay: "0.26s" }}
        >
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-3">Important Notes</h2>
            <ul className="text-gray-700 space-y-2">
              {notes.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 w-3 h-3 rounded-full bg-blue-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>

      <style>{`
        .sr { opacity: 0; transform: translateY(24px); transition: opacity 0.55s ease, transform 0.55s ease; }
        .sr-visible { opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default Requirements;